import React from 'react';
import {DataTable} from '@/components/atoms';
import {SELECTION_TYPE} from '@/utils/constants/commonConstants';
import PropTypes from 'prop-types';

/**
 * ModalTableContent component renders a DataTable with various configurations.
 *
 * @param {Object} props - The properties object.
 * @param {Array} props.data - The data to be displayed in the table.
 * @param {Array} props.columns - The columns configuration for the table.
 * @param {string} props.tableHeader - The header text for the table.
 * @param {string} [props.size='medium'] - The size of the table.
 * @param {boolean} [props.isDark=false] - Flag to determine if the table should be in dark mode.
 * @param {Object} props.rowSelection - The current row selection state.
 * @param {Function} props.setRowSelection - Function to update the row selection state.
 * @param {Function} props.setSelectedRowData - Function to set the selected row data.
 * @param {string} [props.selection=SELECTION_TYPE.NONE] - The selection type for the table.
 *
 * @returns {JSX.Element} The rendered DataTable component.
 */

const ModalTableContent = ({
  data,
  columns,
  tableHeader,
  size = 'medium',
  isDark = false,
  rowSelection,
  setRowSelection,
  setSelectedRowData,
  selection = SELECTION_TYPE.NONE,
  showColumnFilters
}) => {
  return (
    <DataTable
      columns={columns || []}
      data={data?.items || []}
      isDark={isDark}
      isExpandable={false}
      pageIndex={0}
      pageSize={3}
      tableHeader={tableHeader}
      tableDescription=''
      selection={selection}
      size={size}
      rowSelection={rowSelection}
      setRowSelection={setRowSelection}
      setSelectedRowData={setSelectedRowData}
      showColumnFilters={showColumnFilters}
    />
  );
};

ModalTableContent.propTypes = {
  data: PropTypes.shape({
    items: PropTypes.array.isRequired
  }).isRequired,
  columns: PropTypes.array.isRequired,
  tableHeader: PropTypes.string.isRequired,
  size: PropTypes.string,
  isDark: PropTypes.bool,
  rowSelection: PropTypes.object,
  setRowSelection: PropTypes.func.isRequired,
  setSelectedRowData: PropTypes.func.isRequired,
  selection: PropTypes.string,
  showColumnFilters: PropTypes.bool
};

export default React.memo(ModalTableContent);
